/**
 * usePlacementTestData Hook
 *
 * Manages data fetching and state for placement test editing.
 * Handles:
 * - Fetching placement test data by ID
 * - Managing placement test metadata state
 * - Managing questions state
 * - Fetching programs list
 * - Save/update operations
 */

import {useState, useEffect, useCallback} from 'react';
import {message} from 'antd';
import {QUESTION_TYPE, Asset, AssetType} from '../../../graphql/API';
import type {QuestionWithNewFields} from '../../Questions/QuestionTypes';
import {PlacementTestsService} from '../PlacementTestsService';
import {ProgramsNewService} from '../../Program/ProgramsNewService';
import {QuestionsService} from '../../Questions/QuestionsService';
import {getPlacementTestStaticDescription} from '../../../utils/staticValuesUtils/placementTestStaticValues';

const placementTestsService = new PlacementTestsService();
const programsNewService = new ProgramsNewService();
const questionsService = new QuestionsService();

/**
 * Creates an empty question template for NEW questions.
 */
const createEmptyQuestion = (): QuestionWithNewFields =>
  ({
    id: '', // Will be set by backend when saved
    name: '',
    correctAsset: null, // Assets are optional - start with null
    asset: null, // Assets are optional - start with null
    summary: 'Look and record your answer.',
    description: '',
    text: '',
    textAI: '',
    type: QUESTION_TYPE.READ_SENTENCE,
    program: '',
    difficulty: 'NORMAL',
    choices: [],
    // Initialize empty payload structure (raw API format)
    payload: {
      blanks: [], // Will be populated when user adds CHOICES keywords
      media: [], // Will be populated from asset/correctAsset
    },
  }) as QuestionWithNewFields;

export interface UsePlacementTestDataReturn {
  // PlacementTest metadata state
  placementTestName: string;
  setPlacementTestName: (name: string) => void;
  placementTestDescription: string;
  setPlacementTestDescription: (description: string) => void;
  thumbnail: string;
  setThumbnail: (thumbnail: string) => void;
  media: Array<{type: string; url: string}>;
  placementTestAsset: Asset | undefined;
  setPlacementTestAsset: (asset: Asset | undefined) => void;
  duration: string;
  setDuration: (duration: string) => void;
  startDate: string;
  setStartDate: (startDate: string) => void;
  expirationDate: string;
  setExpirationDate: (expirationDate: string) => void;

  // Questions state
  questions: QuestionWithNewFields[];
  setQuestions: (questions: QuestionWithNewFields[]) => void;
  activeQuestionIndex: number;
  setActiveQuestionIndex: (index: number) => void;
  addNewQuestion: () => void;
  deleteQuestion: (index: number) => Promise<void>;

  // Programs data
  programsList: any[];

  // Original placement test data (for update operations)
  originalPlacementTest: any;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;

  // Operations
  fetchPlacementTestData: (placementTestId: string) => Promise<void>;
  savePlacementTest: () => Promise<void>;
}

/**
 * Custom hook for managing placement test data and operations
 */
export const usePlacementTestData = (
  placementTestId?: string,
): UsePlacementTestDataReturn => {
  // PlacementTest metadata state
  const [placementTestName, setPlacementTestName] = useState<string>('');
  const [placementTestDescription, setPlacementTestDescription] =
    useState<string>('');
  const [thumbnail, setThumbnail] = useState<string>('');
  const [media, setMedia] = useState<Array<{type: string; url: string}>>([]);
  const [placementTestAsset, setPlacementTestAsset] = useState<
    Asset | undefined
  >(undefined);
  const [hasAssetBeenModified, setHasAssetBeenModified] =
    useState<boolean>(false);
  const [duration, setDuration] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [expirationDate, setExpirationDate] = useState<string>('');

  // Questions state
  const [questions, setQuestions] = useState<QuestionWithNewFields[]>([
    createEmptyQuestion(),
  ]);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(0);

  // Programs data
  const [programsList, setProgramsList] = useState<any[]>([]);

  // Original placement test data
  const [originalPlacementTest, setOriginalPlacementTest] = useState<any>(null);

  // Loading states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  /**
   * Ensures question has proper ID mapping for MongoDB compatibility.
   */
  const processQuestion = (
    question: QuestionWithNewFields,
  ): QuestionWithNewFields => {
    // Make a shallow copy to avoid mutating the original
    const processedQuestion = {...question};

    // Ensure both id and _id are set for MongoDB compatibility
    if (!processedQuestion.id && !processedQuestion._id) {
      console.error('Question missing both id and _id:', processedQuestion);
    } else if (processedQuestion._id && !processedQuestion.id) {
      processedQuestion.id = processedQuestion._id;
    } else if (processedQuestion.id && !processedQuestion._id) {
      processedQuestion._id = processedQuestion.id;
    }

    return processedQuestion;
  };

  /**
   * Fetches placement test data by ID and initializes state
   */
  const fetchPlacementTestData = useCallback(
    async (placementTestId: string) => {
      try {
        setIsLoading(true);

        const placementTestData: any =
          await placementTestsService.getPlacementTestById(placementTestId);

        // Fetch questions details if needed
        const questionsDetails = await Promise.all(
          placementTestData.questions.map((q: any) => {
            if (typeof q === 'string') {
              // If question is just an ID, fetch it
              return questionsService.getQuestionById(q);
            }
            return Promise.resolve(q);
          }),
        );

        console.log('Raw questions data from fetch:', questionsDetails);

        // Process all questions
        const processedQuestions = questionsDetails.map((q: any) =>
          processQuestion(q as QuestionWithNewFields),
        );

        console.log('Processed questions data:', processedQuestions);

        // Update state
        setOriginalPlacementTest(placementTestData);
        setPlacementTestName(placementTestData.name);

        // Set description using static value based on asset type
        const staticDescription = getPlacementTestStaticDescription(
          placementTestData.asset?.type || 'DEFAULT',
        );
        setPlacementTestDescription(staticDescription);

        setThumbnail(placementTestData.thumbnail || '');
        setMedia(placementTestData.media || []);

        // Set asset directly from service response (service handles transformation)
        setPlacementTestAsset(placementTestData.asset);
        setHasAssetBeenModified(false); // Reset flag when loading fresh data

        setDuration(placementTestData.duration || '');
        setStartDate(placementTestData.startDate || '');
        setExpirationDate(placementTestData.expirationDate || '');
        setQuestions(
          processedQuestions.length > 0
            ? processedQuestions
            : [createEmptyQuestion()],
        );
        setActiveQuestionIndex(0);
      } catch (error) {
        console.error('Error fetching placement test:', error);
        message.error('Failed to load placement test data');
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  /**
   * Fetches programs list on mount
   */
  useEffect(() => {
    programsNewService
      .getNewPrograms()
      .then(programs => {
        console.log('Programs loaded:', programs);
        setProgramsList(programs);
      })
      .catch(error => {
        console.error('Error fetching programs:', error);
        message.error('Failed to load programs list');
      });
  }, []);

  /**
   * Fetches placement test data when placementTestId is provided
   */
  useEffect(() => {
    if (placementTestId) {
      fetchPlacementTestData(placementTestId);
    }
  }, [placementTestId, fetchPlacementTestData]);

  /**
   * Adds a new empty question
   */
  const addNewQuestion = useCallback(() => {
    const newQuestion = createEmptyQuestion();
    setQuestions(prev => [...prev, newQuestion]);
    setActiveQuestionIndex(questions.length);
  }, [questions.length]);

  /**
   * Deletes a question by index
   */
  const deleteQuestion = useCallback(
    async (index: number) => {
      try {
        const questionToDelete = questions[index];
        const questionId = questionToDelete?._id || questionToDelete?.id;

        if (!questionId) {
          throw new Error(
            'Question ID is missing. Cannot delete question without an ID.',
          );
        }

        setIsSaving(true);

        const updatedQuestions = questions.filter((_, i) => i !== index);

        const updatedQuestionIds = updatedQuestions
          .map(q => q._id || q.id)
          .filter(Boolean) as string[];

        await placementTestsService.updatePlacementTestQuestionsList(
          originalPlacementTest.id,
          updatedQuestionIds,
        );

        await questionsService.deleteQuestion(questionId);

        setQuestions(updatedQuestions);

        if (index <= activeQuestionIndex && activeQuestionIndex > 0) {
          setActiveQuestionIndex(activeQuestionIndex - 1);
        }

        message.success('Question deleted successfully');
      } catch (error) {
        console.error('Delete error:', error);
        message.error('Failed to delete question');
      } finally {
        setIsSaving(false);
      }
    },
    [questions, activeQuestionIndex, originalPlacementTest],
  );

  /**
   * Wrapped setter for placementTestAsset that synchronizes media, thumbnail, and description
   */
  const handlePlacementTestAssetChange = useCallback(
    (asset: Asset | undefined) => {
      console.log(
        '🔄 handlePlacementTestAssetChange called with asset:',
        asset,
      );
      console.log('Asset fileUri:', asset?.fileUri);
      console.log('Asset thumbnailUri:', asset?.thumbnailUri);
      console.log('Asset type:', asset?.type);

      setPlacementTestAsset(asset);
      setHasAssetBeenModified(true);

      // Update description with static value based on asset type
      const staticDescription = getPlacementTestStaticDescription(
        asset?.type || 'DEFAULT',
      );
      setPlacementTestDescription(staticDescription);
      console.log('✅ Setting static description:', staticDescription);

      // Synchronize media array and thumbnail with asset
      // For PHOTO/IMAGE, the URL is in thumbnailUri; for VIDEO/AUDIO, it's in fileUri
      const assetUrl = asset?.fileUri || asset?.thumbnailUri;

      if (assetUrl) {
        // Asset uploaded - create media entry
        const assetMedia = {
          type: asset.type?.toLowerCase() || 'video',
          url: assetUrl,
        };
        console.log('✅ Creating media entry:', assetMedia);
        setMedia([assetMedia]);

        // Set thumbnail (for images, use the same URL; for videos, use thumbnailUri)
        const assetThumbnail = asset.thumbnailUri || asset.fileUri || '';
        console.log('✅ Setting thumbnail:', assetThumbnail);
        setThumbnail(assetThumbnail);
      } else {
        // Asset removed - clear media and thumbnail
        console.log('❌ No asset URL - clearing media and thumbnail');
        setMedia([]);
        setThumbnail('');
      }
    },
    [],
  );

  /**
   * Saves the placement test (update operation)
   */
  const savePlacementTest = useCallback(async () => {
    try {
      if (!originalPlacementTest) {
        throw new Error('No original placement test data to update');
      }

      setIsSaving(true);

      // If asset was modified, construct media and thumbnail from asset directly
      // This avoids stale state issues when save is clicked immediately after upload
      let finalMedia = media;
      let finalThumbnail = thumbnail;

      if (hasAssetBeenModified && placementTestAsset) {
        const assetUrl =
          placementTestAsset.fileUri || placementTestAsset.thumbnailUri;
        if (assetUrl) {
          finalMedia = [
            {
              type: placementTestAsset.type?.toLowerCase() || 'video',
              url: assetUrl,
            },
          ];
          finalThumbnail =
            placementTestAsset.thumbnailUri || placementTestAsset.fileUri || '';
        } else {
          // Asset was removed
          finalMedia = [];
          finalThumbnail = '';
        }
      }

      const placementTestData: any = {
        id: originalPlacementTest.id,
        _id: originalPlacementTest._id,
        updatedAt: originalPlacementTest.updatedAt,
        questions: questions,
        name: placementTestName,
        description: placementTestDescription,
        // Include asset only if user modified it via CreateAssetBlock
        ...(hasAssetBeenModified && {asset: placementTestAsset}),
        // Use computed media and thumbnail
        media: finalMedia,
        thumbnail: finalThumbnail,
        duration: duration,
        startDate: startDate,
        expirationDate: expirationDate,
      };

      console.log('Saving placement test data:', placementTestData);
      console.log('Asset modified?', hasAssetBeenModified);
      console.log('Final media:', finalMedia);
      console.log('Final thumbnail:', finalThumbnail);

      // Update placement test
      const response = await placementTestsService.updatePlacementTest(
        placementTestData,
        originalPlacementTest,
      );

      message.success(
        `Placement test updated successfully! ID: ${response?.id}`,
      );

      // Return response for navigation or other actions
      return response;
    } catch (error) {
      console.error('Error saving placement test:', error);
      message.error('Failed to save placement test');
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [
    originalPlacementTest,
    questions,
    placementTestName,
    placementTestDescription,
    placementTestAsset,
    media,
    hasAssetBeenModified,
    thumbnail,
    duration,
    startDate,
    expirationDate,
  ]);

  return {
    // PlacementTest metadata
    placementTestName,
    setPlacementTestName,
    placementTestDescription,
    setPlacementTestDescription,
    thumbnail,
    setThumbnail,
    media,
    placementTestAsset,
    setPlacementTestAsset: handlePlacementTestAssetChange,
    duration,
    setDuration,
    startDate,
    setStartDate,
    expirationDate,
    setExpirationDate,

    // Questions
    questions,
    setQuestions,
    activeQuestionIndex,
    setActiveQuestionIndex,
    addNewQuestion,
    deleteQuestion,

    // Programs
    programsList,

    // Original data
    originalPlacementTest,

    // Loading states
    isLoading,
    isSaving,

    // Operations
    fetchPlacementTestData,
    savePlacementTest,
  };
};
